import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { useDineCourses, useDineMenuGroups } from 'hooks/api/useDineOps';
import { getActiveDineOutletName } from 'auth/dineOutlet';
import type { DineOutletItem, DineOutletItemQuery } from 'api/types';
import DineOpsOutletItemAddModal from 'components/modals/modules/dineops-module/DineOpsOutletItemAddModal';
import DineOpsOutletItemEditModal from 'components/modals/modules/dineops-module/DineOpsOutletItemEditModal';

/**
 * Outlet Items (DO-312, spec 2.36 §7.5) — the dishes the header outlet sells.
 *
 * The menu is one master for the tenant (Menu Items). This screen lists master
 * dishes in the outlet chosen in the header and sets what is the outlet's own:
 * its price, a department here, sold out here. Removing a dish here never
 * touches the master or another outlet. Switching outlet in the header remounts
 * the screen for the new outlet.
 *
 * Paged and searched on the server, as every grid (the master runs to hundreds).
 */

interface Filters { courseId?: string; groupId?: string; show?: string; includeInactive?: string }

/** A grid row is keyed by the dish; the outlet listing id (absent until sold here) rides along. */
type Row = Omit<DineOutletItem, 'id'> & { id: string; listingId?: string };
const toRow = (r: DineOutletItem): Row => ({ ...r, id: r.itemId, listingId: r.id });
const toItem = (r: Row): DineOutletItem => ({ ...r, id: r.listingId });

const money = (n?: number | null) => (n == null ? '—' : Number(n).toFixed(2));

const DineOpsOutletItemsScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const outletName = useMemo(() => getActiveDineOutletName() || 'this outlet', []);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [query, setQuery] = useState<DineOutletItemQuery>({ page: 1, pageSize: 25 });

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<DineOutletItem | null>(null);
  const [removing, setRemoving] = useState<Row | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = allowedActions.includes('delete');

  const { data: courses } = useDineCourses({ pageSize: 100 });
  const { data: groups } = useDineMenuGroups({ pageSize: 200 });

  // Search is server side, debounced.
  useEffect(() => {
    const t = setTimeout(() => setQuery((q) => ({ ...q, searchTerm: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setQuery((q) => ({
      ...q,
      page: 1,
      courseId: filters.courseId || undefined,
      groupId: filters.groupId || undefined,
      show: (filters.show as DineOutletItemQuery['show']) || undefined,
      includeInactive: filters.includeInactive === 'true' || undefined,
    }));
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await dineOpsService.getOutletItems(query);
      setRows(res.data.map(toRow));
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? 'The dishes could not be loaded.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  const courseOptions = useMemo(() => courses.map((c) => ({ value: String(c.id), label: c.courseName })), [courses]);
  const groupOptions = useMemo(() => {
    const visible = filters.courseId ? groups.filter((g) => String(g.courseId) === filters.courseId) : groups;
    return visible.map((g) => ({ value: String(g.id), label: `${g.courseName} · ${g.groupName}` }));
  }, [groups, filters.courseId]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const header = ['Code', 'Dish', 'Category', 'Group', 'Base price', 'Price here', 'Kitchen station', 'Sold out here', 'Status'];
    const lines = rows.map((r) => [r.itemCode ?? '', r.itemName, r.courseName ?? '', r.groupName ?? '', money(r.basePrice),
      r.price == null ? '' : money(r.price), r.effectiveStationName ?? '', r.isSoldOutHere ? 'Yes' : 'No',
      !r.isListed ? 'Not sold here' : r.isActive ? 'On sale' : 'Off'])
      .map((cells) => cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
    const url = URL.createObjectURL(new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `outlet-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const handleRemove = useCallback(async () => {
    if (!removing?.listingId) return;
    setIsRemoving(true);
    try {
      await dineOpsService.removeOutletItem(removing.listingId, removing.rowVersion);
      notificationService.toast.success(`"${removing.itemName}" is no longer sold in ${outletName}. It is still on the menu.`);
      setRemoving(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The dish could not be removed from this outlet.');
    } finally {
      setIsRemoving(false);
    }
  }, [removing, load, outletName]);

  const columns = useMemo((): SyutGridColumn<Row>[] => [
    { id: 'itemCode', header: 'Code', accessorKey: 'itemCode', enableSorting: true, size: 100 },
    { id: 'itemName', header: 'Dish', accessorKey: 'itemName', enableSorting: true, size: 200,
      // A size sits under its dish (DO-314).
      cell: ({ row }) => (row.original.parentItemId
        ? <span data-size-of={row.original.parentItemId} title={row.original.itemName} style={{ paddingLeft: 18 }}>↳ {row.original.variantName}</span>
        : row.original.itemName) },
    { id: 'courseName', header: 'Category', accessorKey: 'courseName', enableSorting: true, size: 130 },
    { id: 'groupName', header: 'Group', accessorKey: 'groupName', enableSorting: true, size: 150 },
    { id: 'basePrice', header: 'Menu price', accessorKey: 'basePrice', enableSorting: true, size: 110,
      cell: ({ row }) => (row.original.isOpenPrice ? <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Open</span> : money(row.original.basePrice)) },
    { id: 'price', header: 'Price here', accessorKey: 'price', enableSorting: true, size: 110,
      cell: ({ row }) => ((row.original.sizeCount ?? 0) > 0
        ? <span style={{ color: 'var(--text-muted)' }}>by size</span>
        : row.original.price == null
        ? <span style={{ color: 'var(--text-muted)' }}>menu price</span>
        : <strong>{money(row.original.price)}</strong>) },
    { id: 'effectiveStationName', header: 'Kitchen station', accessorKey: 'effectiveStationName', enableSorting: true, size: 170,
      cell: ({ row }) => {
        const r = row.original;
        if (r.parentItemId) return <span style={{ color: 'var(--text-muted)' }}>as the dish</span>;
        const label = `${r.effectiveStationName ?? '—'}${r.stationId ? ' (here)' : ''}`;
        return r.stationAllowed || r.itemType === 'OTHER'
          ? label
          : <span className="text-danger" title="This outlet does not use this kitchen station">{label} · not used here</span>;
      } },
    { id: 'isSoldOutHere', header: 'Availability', accessorKey: 'isSoldOutHere', enableSorting: true, size: 130,
      cell: ({ row }) => (row.original.isSoldOutEverywhere
        ? <span className="text-danger">Sold out everywhere</span>
        : row.original.isSoldOutHere ? <span className="text-danger">Sold out here</span> : 'Available') },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 120,
      cell: ({ row }) => (!row.original.isListed
        ? <span style={{ color: 'var(--text-muted)' }}>Not sold here</span>
        : row.original.isActive ? 'On sale' : 'Off') },
  ], []);

  const getRowActions = useCallback((row: Row): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (!row.isListed) {
      if (canAdd) items.push({ id: 'add', label: `Sell in ${outletName}`, icon: <IconSystem.add size={16} />,
        onClick: async () => {
          try {
            const r = await dineOpsService.addOutletItems({ itemIds: [row.itemId] });
            if (r.refused.length) notificationService.toast.warning(`${row.itemName}: ${r.refused[0].reason}`);
            else notificationService.toast.success(`"${row.itemName}" is now sold in ${outletName}.`);
            void load();
          } catch (err: any) {
            notificationService.toast.error(err?.response?.data?.message ?? 'The dish could not be added.');
          }
        } });
      return items;
    }
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setEditing(toItem(row)) });
    if (canDelete) items.push({ id: 'remove', label: row.parentItemId ? `Stop selling this size in ${outletName}` : `Stop selling in ${outletName}`,
      icon: <IconSystem.trash size={16} />, onClick: () => setRemoving(row), variant: 'danger' });
    return items;
  }, [canAdd, canEdit, canDelete, outletName, load]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setAddOpen(true), tooltip: 'Add dishes', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh data', variant: 'icon' });
    b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    return b;
  }, [canAdd, load, handleExport]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Outlet Items"
        description={`The dishes ${outletName} sells, at its own price. The dishes themselves are kept once, on Menu Items.`}
        icon={<IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search dish, code, group or category' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={rows}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={total}
              currentPage={(query.page ?? 1) - 1}
              pageSize={query.pageSize ?? 25}
              onPageChange={(i: number) => setQuery((q) => ({ ...q, page: i + 1 }))}
              onPageSizeChange={(size: number) => setQuery((q) => ({ ...q, pageSize: size, page: 1 }))}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={loadError ?? (filters.show === 'unlisted'
                ? 'Every dish on the menu is already sold here.'
                : `${outletName} sells no dishes yet. Use + to add dishes from the menu.`)}
            />
          </div>
        </div>

        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Show" options={[
                { value: 'listed', label: 'Sold here' },
                { value: 'unlisted', label: 'Not sold here yet' },
                { value: 'all', label: 'Whole menu' },
              ]} value={filters.show ?? ''} onChange={(v) => setFilters((p) => ({ ...p, show: (v as string) || undefined }))}
                placeholder="Sold here" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Category" options={courseOptions} value={filters.courseId ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, courseId: (v as string) || undefined, groupId: undefined }))}
                placeholder="All categories" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Menu group" options={groupOptions} value={filters.groupId ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, groupId: (v as string) || undefined }))}
                placeholder="All groups" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Include switched off" options={[{ value: 'true', label: 'Yes' }]} value={filters.includeInactive ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, includeInactive: (v as string) || undefined }))}
                placeholder="On sale only" size="sm" isClearable />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setFilters({}); setSearchQuery(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <DineOpsOutletItemAddModal isOpen={addOpen} outletName={outletName} courses={courses} groups={groups}
        onClose={() => setAddOpen(false)} onAdded={() => void load()} />
      <DineOpsOutletItemEditModal isOpen={!!editing} record={editing} outletName={outletName}
        onClose={() => setEditing(null)} onSaved={() => void load()} />
      <ConfirmDialog
        open={!!removing}
        title={`Stop selling in ${outletName}?`}
        message={removing?.parentItemId
          ? `"${removing?.itemName ?? ''}" comes off the ${outletName} card; the dish's other sizes stay. Past bills are unaffected.`
          : `"${removing?.itemName ?? ''}" comes off the ${outletName} card${(removing?.sizeCount ?? 0) > 0 ? ', with all its sizes' : ''}. The dish stays on the menu and in other outlets; past bills are unaffected.`}
        confirmLabel="Stop selling"
        cancelLabel="Keep it"
        variant="danger"
        loading={isRemoving}
        loadingMessage="Removing..."
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

export default DineOpsOutletItemsScreen;
